import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { processClaimAI, saveClaimAI } from "../../api/ClaimAPI";
import { ClaimAIDto } from "../../types";
import { useQueryClient } from "@tanstack/react-query";
import SentimentIndicator from "../../../../components/SentimentIndicatorSelector";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useCurrentClient } from "../../../auth/api/getCurrentClient";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import GenerateAlertSelector from "../../../../components/GenerateAlertSelctor";

interface ProcessDocumentProps {
    setIsStepProcessDocument: (isStepProcessDocument: boolean) => void;
    setIsStepSaveResult: (isStepSaveResult: boolean) => void;
    isStepProcessDocument: boolean;
    isStepSaveResult: boolean;
    setMessageError: (messageError: string) => void;
}

export default function ProcessDocument({ setIsStepProcessDocument, setIsStepSaveResult, isStepProcessDocument, isStepSaveResult, setMessageError }: ProcessDocumentProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: currentClient } = useCurrentClient();
    const claimId = useSelector((state: RootState) => state.claimForm.data.id);

    const [claimAiDto, setClaimAiDto] = useState<ClaimAIDto>({
        claimId: claimId,
        feelingNote: null,
        topics: [],
        alertScore: null,
        messageAI: "",
        recommendationAI: "",
        name: "",
        hasAlert: false
    })
    const [loading, setLoading] = useState(false);

    const handleProccessClaim = async () => {
        setLoading(true);
        try {
            const claimAIDto = await processClaimAI(claimId);
            setClaimAiDto(claimAIDto);
            setLoading(false);
            setIsStepProcessDocument(true);
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            setMessageError(fetchError?.message ?? t("CommonError"))
            setLoading(false);
        }
    }

    const invalidateQueryDashboard = async () => {
        queryClient.invalidateQueries({
            queryKey: ['getHistoricClaims'],
            exact: false,
            refetchType: 'all',
        });
        queryClient.invalidateQueries({
            queryKey: ['claimStatus'],
            exact: false,
            refetchType: 'all',
        });
        queryClient.invalidateQueries({
            queryKey: ['ClaimStatusLineChartData'],
            exact: false,
            refetchType: 'all',
        });
        queryClient.invalidateQueries({
            queryKey: ['ClaimServiceIndicatorBarChartData'],
            exact: false,
            refetchType: 'all',
        });
    } 

    const handleSaveResults = async  () => {
        setIsStepSaveResult(true);
        setLoading(true);
        try {
            await saveClaimAI(claimAiDto);
            setLoading(false);
            invalidateQueryDashboard();
            navigate('/claims/historic', { state: { message: t("claim:newClaimSaveSuccessfully") } });

        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            setMessageError(fetchError?.message ?? t("CommonError"))
            setLoading(false);
        }
    }

    const handleSaveWithoutProccessAI = () => {
        invalidateQueryDashboard();
        navigate('/claims/historic', { state: { message: t("claim:newClaimSaveSuccessfully") } });
    }

    const updateField = (
        field: keyof ClaimAIDto,
        value: ClaimAIDto[keyof ClaimAIDto]
    ) => {
        setClaimAiDto(prev => ({
            ...prev,
            [field]: value
        }));
    }

    return (<>
        {loading && (
            <div className="overlay-spinner">
                <Spinner animation="border" variant="light" />
            </div>
        )}
        <Row>
            <Form className="mb-4">
                <Row className="mt-3">
                    <Col className="d-flex justify-content-end" >
                        {!isStepProcessDocument && (
                            <>
                                <Button variant="secondary" className="mr-4" onClick={() => handleSaveWithoutProccessAI()}>{t("claim:createClaimButtonWithoutProcessing")}</Button>
                                <Button variant="primary" onClick={() => handleProccessClaim()}>{t("claim:newClaimButtonProcessClaim")}</Button>
                            </>
                        )}
                        {isStepProcessDocument && !isStepSaveResult && (
                            <Button variant="primary" onClick={() => handleSaveResults()}>{t("claim:createClaimButtonResults")}</Button>)
                        }
                    </Col>    
                </Row>
                <Row>
                    <Col xs={12} md={12} className="mt-4">
                        <h5>{t("claim:newClaimHeaderFeelingAnalysis")}</h5>
                    </Col>
                </Row>
                <Row>
                    <Col xs={6} md={6}>
                        <SentimentIndicator
                            value={claimAiDto.feelingNote}
                            onChange={(value) => updateField('feelingNote', value)}
                            label={t("claim:newClaimFeelingNote")}
                            minValue={currentClient?.sentimentMinimumValue}
                            maxValue={currentClient?.sentimentMaximumValue}
                            disabled={true}
                        />
                    </Col>
                    <Col xs={6} md={6}>
                        <GenerateAlertSelector
                            value={claimAiDto.alertScore}
                            label={t("claim:newClaimGenerateAlert")}
                            minValue={currentClient?.sentimentMinimumValue}
                            maxValue={currentClient?.sentimentMaximumValue}
                            neutralReference={currentClient?.neutralReference}
                            alertMinimumValue={currentClient?.alertMinimumValue}
                            alertMaximumValue={currentClient?.alertMaximumValue}
                            onChange={(value) => updateField('alertScore', value)}
                            disabled={true}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={12} className="mt-4">
                        <h5>{t("claim:newClaimHeaderTopics")}</h5>
                    </Col>
                </Row>
                    {claimAiDto.topics && claimAiDto.topics.length > 0 ? (
                        claimAiDto.topics.map((topic, index) => (
                            <Row key={index}>
                                <Col xs={3} md={3}>
                                    <Form.Group>
                                        <Form.Label>{t("topic:topic")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            readOnly={true}
                                            value={topic.genericTopicName}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={3} md={3}>
                                    <Form.Group>
                                        <Form.Label>{t("topic:subtopic")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            readOnly={true}
                                            value={topic.topicName}
                                        />
                                    </Form.Group>
                                </Col>
                                {/* TODO: Añadir el 3r nivel de jerarquía */}
                                <Col xs={3} md={3}>
                                    <SentimentIndicator
                                        value={topic.feelingNote || null}
                                        disabled={true}
                                        label={t("claim:newClaimFeelingNote")}
                                        minValue={currentClient?.sentimentMinimumValue}
                                        maxValue={currentClient?.sentimentMaximumValue}
                                    />
                                </Col>
                                <Col xs={3} md={3}>
                                    <GenerateAlertSelector
                                        value={topic.alertScore || null}
                                        label={t("claim:newClaimGenerateAlert")}
                                        minValue={currentClient?.sentimentMinimumValue}
                                        maxValue={currentClient?.sentimentMaximumValue}
                                        neutralReference={currentClient?.neutralReference}
                                        alertMinimumValue={currentClient?.alertMinimumValue}
                                        alertMaximumValue={currentClient?.alertMaximumValue}
                                        disabled={true}
                                    />
                                </Col>
                            </Row>
                        ))
                    ) : (
                        <Row>
                            {/* contenido cuando no hay topics */}
                        </Row>
                    )}               
                <Row>
                    <Col xs={12} md={12}>
                        <Form.Group>
                            <Form.Label>{t("claim:newClaimMessageAI")}</Form.Label>
                            <Form.Control
                                type="text"
                                readOnly={true}
                                value={claimAiDto.messageAI}
                                onChange={(e) => updateField('messageAI', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={12}>
                        <Form.Group>
                            <Form.Label>{t("claim:newClaimRecomendationAI")}</Form.Label>
                            <Form.Control
                                type="text"
                                readOnly={true}
                                value={claimAiDto.recommendationAI}
                                onChange={(e) => updateField('recommendationAI', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Form>
        </Row>
    </>)
};

